/**
 * The flock, for members. Same roster as the public /flock/ page — same
 * query, same shaping (see services/api/src/livestock-public.ts) — reached
 * through /member/v1/flock instead, which sits behind the bearer session
 * token. Nothing about the rendering below is a boundary; the route is.
 */

import { useEffect, useState } from 'preact/hooks';
import { Link } from 'wouter-preact';
import type { JSX } from 'preact';

import { apiGet, ApiError } from '../api.js';

export interface FlockAnimal {
  slug: string | null;
  name: string | null;
  species: string;
  blurb: string | null;
  photo_url: string | null;
}

export function FlockView(): JSX.Element {
  const [flock, setFlock] = useState<FlockAnimal[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiGet<{ flock: FlockAnimal[] }>('/member/v1/flock')
      .then((result) => setFlock(result.flock))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not load the flock.'));
  }, []);

  if (error) {
    return (
      <main class="view centred">
        <p class="error" role="alert">{error}</p>
      </main>
    );
  }

  return (
    <main class="view">
      <header>
        <span class="ff-eyebrow">Familiar faces</span>
        <h1>The flock</h1>
        <p class="muted">Who lives here, and what they've been up to.</p>
      </header>

      {flock === null && <p class="muted" role="status">Loading…</p>}
      {flock?.length === 0 && <p class="muted">Nobody has a page yet — check back soon.</p>}

      <div class="flock-grid">
        {flock?.map((animal) => {
          const name = animal.name ?? 'Unnamed';
          const card = (
            <>
              {animal.photo_url ? <img src={animal.photo_url} alt={name} loading="lazy" /> : <span class="animal-placeholder" aria-hidden="true">{name.slice(0, 1)}</span>}
              <div>
                <span class="ff-eyebrow">{animal.species}</span>
                <span class="card-title">{name}</span>
                {animal.blurb && <span class="card-sub">{animal.blurb}</span>}
              </div>
            </>
          );
          return animal.slug ? (
            <Link key={animal.slug} href={`/flock/${encodeURIComponent(animal.slug)}`} class="flock-card">
              {card}
            </Link>
          ) : (
            <div key={name} class="flock-card">
              {card}
            </div>
          );
        })}
      </div>

      <p style="margin-top: 2rem;">
        <Link href="/account">← Your account</Link>
      </p>
    </main>
  );
}
